import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { permissionsFor } from "../lib/txnPermissions";

/**
 * Grant-aware editor used by the PENDING worklist (mirrors backend
 * server.py _allowed_for() via the shared src/lib/txnPermissions.js).
 *
 * - Creator (holding TXN_APV or TXN_PAY): full edit of own row; on save the
 *   pay_tp is FORCED back to 0 (PENDING) so it requires re-approval.
 * - TXN_APV (not creator): may set pay_tp to 1 or 2 only.
 * - TXN_PAY: may set pay_tp to 3/4/5/6 (cash/check/ACH/card), full field edit.
 * - TXN_ADM / SUPER: full edit of every field, pay_tp dropdown shows all options.
 */
export default function PendingEditModal({ txn, profile, calSeq, fyRange, onClose, onSaved }) {
  const [refs, setRefs] = useState(null);
  const [form, setForm] = useState({
    txn_inv:  txn.txn_inv  || "",
    txn_amt:  txn.txn_amt != null ? Number(txn.txn_amt).toFixed(2) : "",
    txn_chk:  txn.txn_chk  || "",
    txn_note: txn.txn_note || "",
    req_init: txn.req_init || "",
    pye_id:   txn.pye_id   || "",
    codes_id: txn.codes_id || "",
    pay_tp:   String(txn.pay_tp ?? "0"),
    pay_dt:   txn.pay_dt   || "",
    post_dt:  txn.post_dt  || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState("");

  useEffect(() => { api.refs().then(setRefs).catch(() => {}); }, []);

  const { editable, allowedPayTps } = useMemo(
    () => permissionsFor(txn, profile),
    [txn, profile]
  );
  const can = (k) => editable.has(k);
  // Self-resubmit: creator resetting their own row back to PENDING.
  const isSelfResubmit = allowedPayTps !== null && allowedPayTps.size === 1 && allowedPayTps.has(0);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Allowed pay_tp options the dropdown should expose
  const payTpOptions = useMemo(() => {
    if (!refs) return [];
    const all = refs.payments || [];
    if (allowedPayTps === null) return all; // Admin/SUPER — unrestricted
    return all.filter((p) => allowedPayTps.has(p.id));
  }, [refs, allowedPayTps]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const patch = {};
      if (isSelfResubmit) {
        patch.pay_tp = 0;       // re-edit forces back to PENDING
      } else if (can("pay_tp") && allowedPayTps) {
        const v = Number(form.pay_tp);
        if (!allowedPayTps.has(v)) {
          setErr("Pick a valid status for this action");
          setBusy(false); return;
        }
        if (v !== Number(txn.pay_tp)) patch.pay_tp = v;
      } else if (can("pay_tp") && Number(form.pay_tp) !== Number(txn.pay_tp)) {
        patch.pay_tp = Number(form.pay_tp); // Admin/SUPER override
      }

      // Other editable fields — only include if they changed
      const fieldMap = {
        txn_inv:  form.txn_inv,
        txn_chk:  form.txn_chk,
        txn_note: form.txn_note,
        req_init: form.req_init,
        pye_id:   form.pye_id,
        codes_id: form.codes_id,
        pay_dt:   form.pay_dt,
        post_dt:  form.post_dt,
      };
      for (const k of Object.keys(fieldMap)) {
        if (!can(k)) continue;
        const cur = (txn[k] ?? "") + "";
        const next = (fieldMap[k] ?? "") + "";
        if (cur !== next) patch[k] = fieldMap[k] === "" ? null : fieldMap[k];
      }
      if (can("txn_amt")) {
        const curAmt = String(txn.txn_amt ?? "");
        if (form.txn_amt !== curAmt) patch.txn_amt = Number(form.txn_amt);
      }

      if (Object.keys(patch).length === 0) { onClose(); return; }

      await api.updateTransaction(txn.txn_seq, patch);
      onSaved();
    } catch (e2) {
      setErr(e2.response?.data?.detail || e2.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const inFyRange = !fyRange || (form.pay_dt >= fyRange.from && form.pay_dt <= fyRange.to);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-card pending-edit-modal"
           onClick={(e) => e.stopPropagation()}
           data-testid="pending-edit-modal"
           style={{ maxWidth: 1196 }}>
        <div className="modal-head">
          <h2>{txn.pay_lit || "Transaction"} · txn #{txn.txn_seq}</h2>
          <button className="modal-close" onClick={onClose}
                  aria-label="Close" data-testid="pem-close">×</button>
        </div>

        <form className="modal-form" onSubmit={submit}>
          {isSelfResubmit && (
            <div data-testid="pem-member-banner"
                 style={{ background: "#fff7e0", border: "1px solid #f0c14b",
                          padding: "8px 12px", borderRadius: 6, fontSize: 13,
                          color: "#5a4400", marginBottom: 12 }}>
              Saving this entry will reset it to <b>PENDING</b> for re-approval.
            </div>
          )}

          <div className="form-grid">
            <div className="form-field">
              <label>Request Date</label>
              <input data-testid="pem-postdt"
                     type="date"
                     value={form.post_dt}
                     disabled={!can("post_dt")}
                     onChange={(e) => set("post_dt", e.target.value)} />
            </div>
            <div className="form-field">
              <label>Requestor</label>
              <select data-testid="pem-req"
                      value={form.req_init}
                      disabled={!can("req_init")}
                      onChange={(e) => set("req_init", e.target.value)}>
                <option value="">—</option>
                {refs?.requestors.map((r) => (
                  <option key={r.id} value={r.id}>{r.label} ({r.id})</option>
                ))}
              </select>
            </div>
            <div className="form-field" style={{ gridColumn: "span 2" }}>
              <label>Payee</label>
              <select data-testid="pem-pye"
                      value={form.pye_id}
                      disabled={!can("pye_id")}
                      onChange={(e) => set("pye_id", e.target.value)}>
                <option value="">—</option>
                {refs?.payees.map((p) => (
                  <option key={p.id} value={p.id}>{p.label} ({p.id})</option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label>Code</label>
              <select data-testid="pem-code"
                      value={form.codes_id}
                      disabled={!can("codes_id")}
                      onChange={(e) => set("codes_id", e.target.value)}>
                <option value="">—</option>
                {refs?.codes.map((c) => (
                  <option key={c.id} value={c.id}>{c.id} · {c.label}</option>
                ))}
              </select>
            </div>
            <div className="form-field" style={{ gridColumn: "span 2" }}>
              <label>Invoice #</label>
              <input data-testid="pem-inv"
                     value={form.txn_inv}
                     maxLength={20}
                     disabled={!can("txn_inv")}
                     onChange={(e) => set("txn_inv", e.target.value)} />
            </div>
            <div className="form-field">
              <label>Amount</label>
              <input data-testid="pem-amt"
                     type="number" step="0.01"
                     value={form.txn_amt}
                     disabled={!can("txn_amt")}
                     onChange={(e) => set("txn_amt", e.target.value)} />
            </div>

            <div className="form-field">
              <label>Pay Type {isSelfResubmit && <span className="muted">(forced to PENDING)</span>}</label>
              <select data-testid="pem-paytp"
                      value={isSelfResubmit ? "0" : form.pay_tp}
                      disabled={isSelfResubmit || !can("pay_tp")}
                      onChange={(e) => set("pay_tp", e.target.value)}>
                {isSelfResubmit
                  ? <option value="0">0 · PENDING</option>
                  : payTpOptions.map((p) => (
                      <option key={p.id} value={p.id}>{p.id} · {p.label}</option>
                    ))}
              </select>
            </div>
            <div className="form-field">
              <label>Check #</label>
              <input data-testid="pem-chk"
                     value={form.txn_chk}
                     maxLength={5}
                     disabled={!can("txn_chk")}
                     onChange={(e) => set("txn_chk", e.target.value)} />
            </div>
            <div className="form-field" style={{ gridColumn: "span 2" }}>
              <label>Pay Date</label>
              <input data-testid="pem-pdt"
                     type="date"
                     value={form.pay_dt}
                     disabled={!can("pay_dt")}
                     onChange={(e) => set("pay_dt", e.target.value)} />
              {!inFyRange && (
                <span className="form-warn" style={{ fontSize: 11, color: "#b45c00" }}>
                  Outside current fiscal year range
                </span>
              )}
            </div>

            <div className="form-field form-field-wide">
              <label>Note</label>
              <input data-testid="pem-note"
                     value={form.txn_note}
                     maxLength={50}
                     disabled={!can("txn_note")}
                     onChange={(e) => set("txn_note", e.target.value)} />
            </div>
          </div>

          {err && <div className="form-error" data-testid="pem-error">{err}</div>}

          <div className="modal-actions no-divider"
               style={{ justifyContent: "flex-end", gap: 10 }}>
            <button type="button" className="btn-link"
                    data-testid="pem-cancel"
                    onClick={onClose} disabled={busy}>Cancel</button>
            <button type="submit" className="btn-primary"
                    data-testid="pem-save"
                    disabled={busy}
                    style={{ width: "auto", padding: "10px 22px", margin: 0 }}>
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
